if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this._currentIndex = 0;

        if (!this.elements.viewer) return;

        // Cache slider and slides
        this._slider = this.elements.viewer.querySelector('.slider');
        this._slides = this._slider
          ? Array.from(this._slider.querySelectorAll('[data-media-id]'))
          : [];

        this._bindArrows();
        this._bindThumbnails();
        this._bindSliderScroll();

        if (
          this.elements.thumbnails &&
          this.dataset.desktopLayout &&
          this.dataset.desktopLayout.includes('thumbnail') &&
          this.mql.matches
        ) {
          this.removeListSemantic();
        }
      }

      // ─── Arrow buttons ────────────────────────────────────────────────────────

      _bindArrows() {
        const viewer = this.elements.viewer;
        if (!viewer) return;
        viewer.querySelectorAll('.slider-button--prev').forEach((btn) => {
          btn.addEventListener('click', () => this._goToIndex(this._currentIndex - 1));
        });
        viewer.querySelectorAll('.slider-button--next').forEach((btn) => {
          btn.addEventListener('click', () => this._goToIndex(this._currentIndex + 1));
        });
      }

      // ─── Thumbnail clicks ─────────────────────────────────────────────────────

      _bindThumbnails() {
        if (!this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((item) => {
          const btn = item.querySelector('button');
          if (btn) {
            btn.addEventListener('click', () => {
              this.setActiveMedia(item.dataset.target, false);
            });
          }
        });
      }

      // ─── Native scroll tracking (keeps arrows in sync with swipe) ────────────

      _bindSliderScroll() {
        if (!this._slider) return;
        let scrollTimer;
        this._slider.addEventListener('scroll', () => {
          clearTimeout(scrollTimer);
          scrollTimer = setTimeout(() => {
            const slideWidth = this._slider.clientWidth;
            if (!slideWidth) return;
            const index = Math.round(this._slider.scrollLeft / slideWidth);
            if (index !== this._currentIndex && this._slides[index]) {
              this._currentIndex = index;
              const mediaId = this._slides[index].dataset.mediaId;
              this._syncThumbnail(mediaId);
            }
          }, 80);
        }, { passive: true });
      }

      // ─── Core: go to a slide by index ─────────────────────────────────────────

      _goToIndex(index) {
        if (!this._slides.length) return;
        index = Math.max(0, Math.min(index, this._slides.length - 1));
        this._currentIndex = index;
        const activeMedia = this._slides[index];

        // Update is-active
        this._slides.forEach((s) => s.classList.remove('is-active'));
        activeMedia.classList.add('is-active');

        // Scroll slider to exact slide width × index — reliable snap alignment
        if (this._slider) {
          const slideWidth = this._slider.clientWidth;
          this._slider.scrollTo({ left: slideWidth * index, behavior: 'smooth' });
        }

        this.playActiveMedia(activeMedia);
        this.preventStickyHeader();

        // Sync thumbnail
        const mediaId = activeMedia.dataset.mediaId;
        this._syncThumbnail(mediaId);

        // Scroll page if slide is above viewport
        window.setTimeout(() => {
          const rect = activeMedia.getBoundingClientRect();
          if (rect.top > -0.5) return;
          window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });
        });
      }

      // ─── Public: activate by mediaId (called from thumbnail click) ────────────

      setActiveMedia(mediaId, prepend) {
        const index = this._slides.findIndex(
          (s) => s.dataset.mediaId === mediaId
        );

        if (index === -1) return;

        if (prepend) {
          const activeMedia = this._slides[index];
          const firstSlide = this._slides[0];
          if (firstSlide !== activeMedia) {
            activeMedia.parentElement.prepend(activeMedia);
            // Re-cache slides after DOM change
            this._slides = Array.from(
              this._slider.querySelectorAll('[data-media-id]')
            );
          }

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(
              `[data-target="${mediaId}"]`
            );
            if (activeThumbnail && activeThumbnail.parentElement.firstChild !== activeThumbnail) {
              activeThumbnail.parentElement.prepend(activeThumbnail);
            }
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        // Find new index after potential DOM change
        const newIndex = this._slides.findIndex(
          (s) => s.dataset.mediaId === mediaId
        );
        this._goToIndex(newIndex !== -1 ? newIndex : 0);
      }

      // ─── Sync thumbnail highlight + scroll ────────────────────────────────────

      _syncThumbnail(mediaId) {
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        if (thumbnail) {
          const position = thumbnail.dataset.mediaPosition;
          const slide = this._slides.find((s) => s.dataset.mediaId === mediaId);
          if (slide) this.announceLiveRegion(slide, position);
        }
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((el) => el.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', 'true');

        // Scroll thumbnail strip if needed
        const thumbSlider = this.elements.thumbnails.slider ||
          this.elements.thumbnails.querySelector('.slider');
        if (thumbSlider) {
          const isVisible =
            typeof this.elements.thumbnails.isSlideVisible === 'function'
              ? this.elements.thumbnails.isSlideVisible(thumbnail, 10)
              : false;
          if (!isVisible) {
            thumbSlider.scrollTo({ left: thumbnail.offsetLeft, behavior: 'smooth' });
          }
        }
      }

      // ─── Accessibility live region ────────────────────────────────────────────

      announceLiveRegion(activeItem, position) {
        if (!this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', 'false');
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
            '[index]',
            position
          );
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', 'true');
          }, 2000);
        };
        image.src = image.src;
      }

      // ─── Media playback ───────────────────────────────────────────────────────

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      // ─── Sticky header prevention ─────────────────────────────────────────────

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      // ─── Accessibility: remove list semantics for thumbnail layout ────────────

      removeListSemantic() {
        const slider = this.elements.viewer.slider ||
          this.elements.viewer.querySelector('.slider');
        if (!slider) return;
        slider.setAttribute('role', 'presentation');
        slider.querySelectorAll('.slider__slide').forEach((slide) =>
          slide.setAttribute('role', 'presentation')
        );
      }
    }
  );
}